import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileCheck, ArrowRight, MessageCircle, CheckCircle2,
  Loader2, AlertCircle, Package, Sparkles, Star,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { ordersApi } from '@/services/api';
import { useSettings } from '@/hooks/useSettings';
import { toast } from 'sonner';
import OrderLayout from './OrderLayout';
import { useOrderState } from './useOrderState';
import { useLanguage } from '@/context/LanguageContext';

const InfoRow = ({ label, value }) => (
  <div className="flex items-start gap-4 py-3 border-b border-charcoal/8 last:border-0">
    <span className="text-charcoal/50 text-sm min-w-[140px]">{label}</span>
    <span className="text-charcoal font-medium text-sm flex-1">{value || '—'}</span>
  </div>
);

const AgreementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLang } = useLanguage();
  const isAr = currentLang === 'ar';
  const { order, localData, loading } = useOrderState();
  const { settings } = useSettings();
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!loading && !localData) navigate('/start');
  }, [loading, localData, navigate]);

  if (loading) {
    return (
      <OrderLayout currentStep={4}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-gold" size={32} />
        </div>
      </OrderLayout>
    );
  }

  const agreement = order?.agreement || {};
  const measurements = order?.measurements || {};
  const techVisit = order?.technician_visit || {};
  const showroomVisit = order?.showroom_visit || {};
  const adminPhone = (settings?.whatsapp || '').replace(/[^\d+]/g, '');
  const agreementTemplate = isAr ? settings?.agreement_template_ar : settings?.agreement_template_en;

  const buildWaLink = (msg) => `https://wa.me/${adminPhone}?text=${encodeURIComponent(msg)}`;

  const changeRequestMsg = buildWaLink(
    [
      `مرحباً فريق أرمان كيتشنز 👋`,
      `أرغب في طلب تعديلات على الاتفاقية`,
      `اسم العميل: ${localData?.customerName || ''}`,
      `رقم الطلب: ${localData?.orderNumber || ''}`,
      localData?.accessPassword ? `رمز التحقق: ${localData.accessPassword}` : '',
    ].filter(Boolean).join('\n')
  );

  const handleAccept = async () => {
    if (!accepted) {
      toast.error(t('order.error'));
      return;
    }
    setSubmitting(true);
    try {
      await ordersApi.updateStep(localData.orderId, 4, { accepted: true });
      setDone(true);
      toast.success(t('order.saved'));
    } catch {
      toast.error(t('order.error'));
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Creative done screen ── */
  if (done) {
    return (
      <OrderLayout currentStep={4} orderNumber={localData?.orderNumber}>
        <div className="text-center py-4 space-y-8">
          {/* Animated celebration */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="relative mx-auto w-28 h-28"
          >
            <div className="absolute inset-0 rounded-full bg-gold/15 animate-ping" />
            <div className="relative w-28 h-28 bg-gradient-to-br from-gold/20 to-gold/10 rounded-full flex items-center justify-center border border-gold/30">
              <CheckCircle2 className="text-gold" size={52} />
            </div>
            {/* Stars */}
            {[...Array(4)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute"
                style={{
                  top: i < 2 ? '-8px' : 'auto',
                  bottom: i >= 2 ? '-8px' : 'auto',
                  left: i % 2 === 0 ? '-8px' : 'auto',
                  right: i % 2 !== 0 ? '-8px' : 'auto',
                }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3 + i * 0.1 }}
              >
                <Star size={16} className="text-gold fill-gold" />
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-2xl font-bold text-charcoal mb-2">{t('order.saved')}</h3>
            <p className="text-charcoal/60">{t('order.steps.production.desc')}</p>
          </motion.div>

          {/* Info strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="grid grid-cols-3 gap-3 text-center"
          >
            {[
              { icon: '📐', label: isAr ? 'المقاسات مؤكدة' : 'Measurements set' },
              { icon: '✍️', label: isAr ? 'الاتفاقية موقعة' : 'Agreement signed' },
              { icon: '🏭', label: isAr ? 'التصنيع قريباً' : 'Production soon' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.08 }}
                className="bg-charcoal/3 border border-charcoal/8 p-4"
              >
                <p className="text-2xl mb-1">{item.icon}</p>
                <p className="text-xs text-charcoal/60 font-medium">{item.label}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="relative overflow-hidden border-2 border-gold/20 bg-gradient-to-br from-gold/5 to-gold/12 p-6 text-start"
          >
            <div className="absolute -top-6 -end-6 w-28 h-28 rounded-full bg-gold/8" />
            <div className="relative flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gold/15 rounded-full flex items-center justify-center flex-shrink-0">
                  <Package size={22} className="text-gold" />
                </div>
                <div>
                  <p className="font-bold text-charcoal">{t('order.steps.production.title')}</p>
                  <p className="text-charcoal/55 text-sm">{t('order.production.desc')}</p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                animate={{ boxShadow: ['0 0 0 0 rgba(239,127,26,0)', '0 0 0 8px rgba(239,127,26,0.18)', '0 0 0 0 rgba(239,127,26,0)'] }}
                transition={{ repeat: Infinity, duration: 2, delay: 0.8 }}
                onClick={() => navigate('/production')}
                className="flex items-center gap-2 bg-gold text-charcoal-deep font-bold px-6 py-3 hover:opacity-90 transition-opacity flex-shrink-0"
              >
                {t('order.steps.production.title')} <ArrowRight size={18} />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </OrderLayout>
    );
  }

  return (
    <OrderLayout currentStep={4} orderNumber={localData?.orderNumber}>
      <div className="space-y-7">
        {/* Order Summary */}
        <div className="bg-white/50 border border-charcoal/8 p-6">
          <p className="label-text mb-4">
            <FileCheck size={12} className="inline me-2" />
            {t('order.steps.agreement.title')}
          </p>
          <InfoRow label={t('order.form.name')} value={order?.customer_name} />
          <InfoRow label={t('order.form.phone')} value={order?.customer_phone} />
          <InfoRow label={t('order.form.address')} value={order?.customer_address} />
          {measurements.length && (
            <InfoRow
              label={t('order.steps.measurements.title')}
              value={`${measurements.length} × ${measurements.width} × ${measurements.height} cm`}
            />
          )}
          {techVisit.date && (
            <InfoRow
              label={t('order.steps.techVisit.title')}
              value={`${techVisit.date} — ${techVisit.time_slot}`}
            />
          )}
          {showroomVisit.date && (
            <InfoRow
              label={t('order.steps.showroom.title')}
              value={`${showroomVisit.date} — ${showroomVisit.time_slot}`}
            />
          )}
          {order?.technician_assigned && (
            <InfoRow label={t('order.technician')} value={order.technician_assigned} />
          )}
        </div>

        {/* Agreement template from admin settings */}
        {agreementTemplate && (
          <div className="bg-white border border-charcoal/10 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={14} className="text-gold" />
              <p className="label-text">{isAr ? 'نص الاتفاقية' : 'Agreement Terms'}</p>
            </div>
            <div
              className="text-charcoal/80 text-sm leading-relaxed prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: agreementTemplate }}
            />
          </div>
        )}

        {/* Admin-filled pricing details */}
        {(agreement.design_details || agreement.total_price) ? (
          <div className="bg-gold/5 border border-gold/20 p-6">
            <p className="label-text mb-4">{t('order.designDetails')}</p>
            {agreement.design_details && (
              <p className="text-charcoal/80 text-sm leading-relaxed mb-3">{agreement.design_details}</p>
            )}
            {agreement.total_price && (
              <p className="text-3xl font-bold text-charcoal">
                {agreement.total_price.toLocaleString()} <span className="text-lg font-normal text-charcoal/60">{t('order.currency')}</span>
              </p>
            )}
            {agreement.notes && (
              <p className="text-charcoal/60 text-sm mt-3 italic">{agreement.notes}</p>
            )}
          </div>
        ) : (
          <div className="flex items-start gap-3 bg-charcoal/5 border border-charcoal/10 p-5">
            <AlertCircle size={18} className="text-charcoal/40 flex-shrink-0 mt-0.5" />
            <p className="text-charcoal/60 text-sm">
              {isAr
                ? 'سيضيف فريقنا تفاصيل التصميم والسعر قريباً بعد زيارة المعرض.'
                : 'Our team will add design details and pricing shortly after your showroom visit.'}
            </p>
          </div>
        )}

        {/* Admin notes */}
        {order?.admin_notes && (
          <div className="bg-charcoal/4 border border-charcoal/10 p-5">
            <p className="text-sm font-medium text-charcoal/60 mb-1">{t('order.adminNotes')}</p>
            <p className="text-charcoal text-sm">{order.admin_notes}</p>
          </div>
        )}

        {/* Accept */}
        <div className="border-t border-charcoal/8 pt-6 space-y-5">
          <label className="flex items-center gap-3 cursor-pointer group">
            <div className={`w-6 h-6 border-2 flex items-center justify-center transition-all ${accepted ? 'border-gold bg-gold' : 'border-charcoal/20 group-hover:border-gold/50'}`}>
              {accepted && <CheckCircle2 size={14} className="text-white" strokeWidth={3} />}
            </div>
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="sr-only"
            />
            <span className="text-charcoal font-medium select-none">{t('order.form.accept')}</span>
          </label>

          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={handleAccept}
              disabled={submitting || !accepted}
              className="bg-gold text-charcoal-deep font-bold py-4 flex items-center justify-center gap-3 hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              {submitting ? (
                <><Loader2 size={18} className="animate-spin" /> {t('order.form.saving')}</>
              ) : (
                <><CheckCircle2 size={18} /> {t('order.form.accept')}</>
              )}
            </button>

            <a
              href={changeRequestMsg}
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-[#25D366]/40 text-charcoal font-bold py-4 flex items-center justify-center gap-3 hover:border-[#25D366] hover:bg-[#25D366]/5 transition-all"
            >
              <MessageCircle size={18} className="text-[#25D366]" />
              {t('order.form.requestChanges')}
            </a>
          </div>
        </div>
      </div>
    </OrderLayout>
  );
};

export default AgreementPage;
